/**
 * Aerona Integrator settings page — 5 tabs (General, Credentials, Practices,
 * Services, Clinicians). Mirrors the WordPress plugin the clinic owner uses
 * today. Practices and Services tabs support add / delete / drag-reorder.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BadgeCheck, AlertCircle, RotateCcw, Save,
  Plus, Trash2, GripVertical, Download, Upload,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../../lib/api";
import { Icon } from "@iconify/react";
import { CATEGORY_ICONS, DEFAULT_CATEGORY_ICON, resolveCategoryIcon } from "../../lib/categoryIcons";

const SUB_TABS = [
  { id: "general",     label: "General" },
  { id: "credentials", label: "Credentials" },
  { id: "practices",   label: "Practices" },
  { id: "categories",  label: "Categories" },
  { id: "services",    label: "Services" },
  { id: "clinicians",  label: "Clinicians" },
];

// Generate a short, human-readable id for a newly added row.
function shortId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function AeronaIntegratorSettings({ site = "dental" }) {
  const [sub, setSub] = useState("general");
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setConfig(null);
    (async () => {
      const cfg = await api.adminConfig();
      if (!cancelled) setConfig(cfg);
    })();
    return () => { cancelled = true; };
  }, [site]);

  const refresh = async () => setConfig(await api.adminConfig());

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.adminUpdateConfig(patch);
      await refresh();
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reseed = async () => {
    if (!window.confirm("Reset all practices, services and mappings to the original defaults? Bookings are NOT affected.")) return;
    await api.adminSeedDefaults();
    await refresh();
    toast.success("Defaults restored");
  };

  // ── Export / Import ─────────────────────────────────────────────────
  // Lets the admin copy an entire site's configuration (practices, services,
  // categories, clinician mappings, general settings) between environments —
  // typically preview → production after adding a new booking site.
  // Aerona credentials are NEVER included in the export (security).
  const exportConfig = () => {
    const snapshot = {
      site,
      exported_at: new Date().toISOString(),
      general: config.general,
      practices: config.practices,
      services: config.services,
      categories: config.categories || [],
      mappings: config.mappings,
    };
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${site}-config-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Config downloaded");
  };

  const fileInputRef = useRef(null);
  const importConfig = () => fileInputRef.current?.click();
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting the same file re-triggers
    if (!file) return;
    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      toast.error("Not a valid JSON file");
      return;
    }
    if (parsed.site && parsed.site !== site) {
      const proceed = window.confirm(
        `This file was exported from the "${parsed.site}" site but you're importing into "${site}". Continue?`
      );
      if (!proceed) return;
    }
    if (!window.confirm(
      `Import will REPLACE all practices, services, categories and clinician mappings for the ${site.toUpperCase()} site with the contents of this file. Bookings are not affected. Continue?`
    )) return;
    await api.adminUpdateConfig({
      general: parsed.general,
      practices: parsed.practices,
      services: parsed.services,
      categories: parsed.categories,
      mappings: parsed.mappings,
    });
    await refresh();
    toast.success("Config imported");
  };

  if (!config) return <div className="text-[#5C5C5C]">Loading…</div>;

  return (
    <div data-testid="aerona-integrator-settings">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={handleImportFile}
        data-testid="config-import-input"
      />
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="text-3xl font-extrabold" style={{ fontFamily: "Manrope" }}>Aerona Integrator Settings</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={exportConfig}
            className="text-xs text-[#5C5C5C] hover:text-[#0466ff] inline-flex items-center gap-1"
            data-testid="export-config-button"
            title="Download this site's config as JSON — use it to copy settings between preview and production."
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Export
          </button>
          <button
            onClick={importConfig}
            className="text-xs text-[#5C5C5C] hover:text-[#0466ff] inline-flex items-center gap-1"
            data-testid="import-config-button"
            title="Upload a config JSON exported from another environment."
          >
            <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /> Import
          </button>
          {site === "dental" && (
            <button
              onClick={reseed}
              className="text-xs text-[#5C5C5C] hover:text-[#0466ff] inline-flex items-center gap-1"
              data-testid="reseed-defaults-button"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} /> Reset to defaults
            </button>
          )}
        </div>
      </div>

      <StatusBanner config={config} />

      <div className="border-b border-[#E0E0E0] mb-6 -mx-1 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {SUB_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                sub === t.id
                  ? "border-[#0466ff] text-[#0466ff]"
                  : "border-transparent text-[#5C5C5C] hover:text-[#1A1A1A]"
              }`}
              style={{ fontFamily: "Manrope" }}
              data-testid={`subtab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {sub === "general" && <GeneralTab config={config} onSave={save} saving={saving} />}
      {sub === "credentials" && <CredentialsTab config={config} onSave={save} saving={saving} />}
      {sub === "practices" && <PracticesTab config={config} onSave={save} saving={saving} />}
      {sub === "categories" && <CategoriesTab config={config} onSave={save} saving={saving} />}
      {sub === "services" && <ServicesTab config={config} onSave={save} saving={saving} />}
      {sub === "clinicians" && <CliniciansTab config={config} onSave={save} saving={saving} />}
    </div>
  );
}

function StatusBanner({ config }) {
  const ready = config.credentials.aerona_configured;
  return (
    <div className={`ws-card p-4 mb-6 ${ready ? "bg-[#E8F0FE]" : "bg-[#FFF4EE]"}`} data-testid="aerona-status-banner">
      <div className="flex items-start gap-3">
        {ready
          ? <BadgeCheck className="w-5 h-5 text-[#0466ff] mt-0.5" strokeWidth={1.5} />
          : <AlertCircle className="w-5 h-5 text-[#C85A41] mt-0.5" strokeWidth={1.5} />}
        <div>
          <div className="font-semibold" style={{ fontFamily: "Manrope" }}>
            {ready ? "Aerona Live Mode" : "Aerona Demo Mode (mock data)"}
          </div>
          <p className="text-xs text-[#5C5C5C] mt-0.5">
            {ready
              ? "Bookings sync to your Aerona Dental account in real time."
              : "Enter your Aerona credentials in the Credentials tab to switch from sample data to live calendars."}
          </p>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────── General ────────────────────────────
function GeneralTab({ config, onSave, saving }) {
  const [form, setForm] = useState(config.general);
  return (
    <div className="max-w-2xl space-y-4" data-testid="general-pane">
      <Field
        label="Thank-you URL (post-booking redirect — keeps your ad-tracking pixels firing)"
        value={form.thank_you_url || ""}
        onChange={(v) => setForm({ ...form, thank_you_url: v })}
        dataTestId="general-thankyou"
      />
      <Field
        label="Online payment discount %"
        type="number"
        value={form.online_discount_pct ?? 10}
        onChange={(v) => setForm({ ...form, online_discount_pct: Number(v) })}
        dataTestId="general-discount"
      />
      <Field
        label="Currency code (lowercase, e.g. eur, gbp, usd)"
        value={form.currency || "eur"}
        onChange={(v) => setForm({ ...form, currency: v })}
        dataTestId="general-currency"
      />
      <SaveBar saving={saving} onClick={() => onSave({ general: form })} testId="general-save" />
    </div>
  );
}

// ──────────────────────────── Credentials ────────────────────────────
function CredentialsTab({ config, onSave, saving }) {
  const c = config.credentials;
  const [form, setForm] = useState({ aerona_username: "", aerona_password: "", aerona_pin: "" });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.adminTestAerona();
      setTestResult(res);
    } catch (e) {
      setTestResult({ ok: false, message: "Couldn't reach the backend." });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="credentials-pane">
      <h3 className="text-lg font-bold text-[#0466ff] mb-1" style={{ fontFamily: "Manrope" }}>Aerona credentials</h3>
      <p className="text-sm text-[#5C5C5C] mb-4">
        Enter the same <strong>username, password and PIN</strong> you use to log into Aerona day-to-day. Leave a field blank to keep its current value.
      </p>
      <Field label={`Username${c.username ? ` · currently: ${c.username}` : ""}`} value={form.aerona_username} onChange={(v) => setForm({ ...form, aerona_username: v })} dataTestId="cred-username" />
      <Field label={`Password${c.password_set ? " · currently set" : ""}`} type="password" value={form.aerona_password} onChange={(v) => setForm({ ...form, aerona_password: v })} dataTestId="cred-password" />
      <Field label={`PIN${c.pin_set ? " · currently set" : ""}`} value={form.aerona_pin} onChange={(v) => setForm({ ...form, aerona_pin: v })} dataTestId="cred-pin" />

      <div className="flex gap-3 mt-6 flex-wrap">
        <button
          onClick={() => {
            const patch = {};
            if (form.aerona_username) patch.aerona_username = form.aerona_username;
            if (form.aerona_password) patch.aerona_password = form.aerona_password;
            if (form.aerona_pin) patch.aerona_pin = form.aerona_pin;
            onSave(patch);
            setForm({ aerona_username: "", aerona_password: "", aerona_pin: "" });
          }}
          disabled={saving}
          className="ws-btn-primary inline-flex items-center gap-2"
          data-testid="cred-save"
        >
          <Save className="w-4 h-4" strokeWidth={2.5} /> {saving ? "SAVING…" : "SAVE CHANGES"}
        </button>
        <button
          onClick={runTest}
          disabled={testing || !c.aerona_configured}
          className="ws-btn-outline inline-flex items-center gap-2"
          data-testid="cred-test"
          title={!c.aerona_configured ? "Save credentials first" : "Live-test the login against aeronaclinic.com"}
        >
          {testing ? "TESTING…" : "TEST CONNECTION"}
        </button>
      </div>

      {testResult && (
        <div className={`mt-5 ws-card p-4 ${testResult.ok ? "bg-[#E8F0FE]" : "bg-[#FFF4EE]"}`} data-testid="cred-test-result">
          <div className="flex items-start gap-3">
            {testResult.ok
              ? <BadgeCheck className="w-5 h-5 text-[#0466ff] mt-0.5" strokeWidth={1.5} />
              : <AlertCircle className="w-5 h-5 text-[#C85A41] mt-0.5" strokeWidth={1.5} />}
            <div>
              <div className="font-semibold" style={{ fontFamily: "Manrope" }}>
                {testResult.ok ? "Aerona is reachable" : "Connection failed"}
              </div>
              <p className="text-sm text-[#5C5C5C] mt-0.5">{testResult.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── Practices (drag-reorder + add/delete) ────────────────────────────
function PracticesTab({ config, onSave, saving }) {
  const [rows, setRows] = useState(() => [...config.practices]);

  const update = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id) => {
    if (!window.confirm("Delete this practice? Any clinician mappings linked to it will also be removed.")) return;
    setRows((rs) => rs.filter((r) => r.id !== id));
  };
  const addRow = () => {
    setRows((rs) => [...rs, { id: shortId("p"), label: "New practice", aerona_practice_id: "", sort_order: (rs.length + 1) }]);
  };

  const handleSave = () => {
    // Re-number sort_order from current array order
    const ordered = rows.map((r, i) => ({ ...r, sort_order: i + 1 }));
    onSave({ practices: ordered });
  };

  return (
    <div data-testid="practices-pane">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h3 className="text-lg font-bold text-[#0466ff]" style={{ fontFamily: "Manrope" }}>Practice IDs</h3>
        <button onClick={addRow} className="ws-btn-outline inline-flex items-center gap-2 text-sm" data-testid="practices-add-row">
          <Plus className="w-4 h-4" strokeWidth={2.5} /> ADD PRACTICE
        </button>
      </div>
      <p className="text-sm text-[#5C5C5C] mb-4">
        Each row is one entry in the customer&apos;s <strong>Location</strong> dropdown. Drag the grip handle to reorder.
      </p>

      <div className="hidden md:grid grid-cols-[28px_1fr_1fr_180px_44px] gap-2 sm:gap-3 items-center px-1 pb-2 mb-1 border-b border-[#E0E0E0]" data-testid="practices-headers">
        <div />
        <div className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Practice Name</div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Subtext (address / eircode)</div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Practice ID</div>
        <div />
      </div>

      <SortableList
        items={rows}
        onReorder={setRows}
        render={(row, dragHandle) => (
          <div className="grid grid-cols-[28px_1fr_1fr_180px_44px] gap-2 sm:gap-3 items-center" data-testid={`practice-row-${row.id}`}>
            {dragHandle}
            <input
              className="ws-input"
              value={row.label}
              onChange={(e) => update(row.id, { label: e.target.value })}
              data-testid={`practice-label-${row.id}`}
            />
            <input
              className="ws-input"
              placeholder="e.g. 12 Main St, Dublin 4 D04 X1Y2"
              value={row.subtext || ""}
              onChange={(e) => update(row.id, { subtext: e.target.value })}
              data-testid={`practice-subtext-${row.id}`}
            />
            <input
              className="ws-input"
              placeholder="Practice ID"
              value={row.aerona_practice_id || ""}
              onChange={(e) => update(row.id, { aerona_practice_id: e.target.value.replace(/[^\d]/g, "") })}
              data-testid={`practice-aeronaid-${row.id}`}
            />
            <button
              onClick={() => remove(row.id)}
              className="p-2 rounded-md hover:bg-[#FFF4EE] text-[#C85A41]"
              title="Delete"
              data-testid={`practice-delete-${row.id}`}
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
      />

      <SaveBar saving={saving} onClick={handleSave} testId="practices-save" />
    </div>
  );
}

// Multi-select category picker for the Services tab. Stores `category_ids: []`.
function CategoryMultiSelect({ value, categories, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = value || [];
  const labelById = Object.fromEntries(categories.map((c) => [c.id, c.label]));
  const labels = selected.map((id) => labelById[id]).filter(Boolean);
  const buttonText = labels.length === 0
    ? "— uncategorised —"
    : labels.length <= 2 ? labels.join(", ") : `${labels.length} categories`;

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`ws-input w-full text-left truncate ${open ? "border-[#0466ff]" : ""}`}
        data-testid={testId}
        title={labels.join(", ")}
      >
        <span className={labels.length ? "text-[#1A1A1A]" : "text-[#5C5C5C]"}>{buttonText}</span>
      </button>
      {open && (
        <div
          className="absolute left-0 mt-1 z-30 bg-white border border-[#E5E5E5] rounded-md shadow-lg w-64 max-h-72 overflow-y-auto"
          data-testid={`${testId}-popover`}
        >
          {categories.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#5C5C5C]">No categories yet — add them in the Categories tab.</div>
          ) : categories.map((c) => {
            const checked = selected.includes(c.id);
            return (
              <label
                key={c.id}
                className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#F0F6FF] ${checked ? "bg-[#F0F6FF]" : ""}`}
                data-testid={`${testId}-option-${c.id}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(c.id)}
                  className="accent-[#0466ff]"
                />
                <span className="text-[#1A1A1A]">{c.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── Services (drag-reorder + add/delete) ────────────────────────────
function ServicesTab({ config, onSave, saving }) {
  const [rows, setRows] = useState(() => [...config.services]);
  const categories = config.categories || [];

  const update = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id) => {
    if (!window.confirm("Delete this service?")) return;
    setRows((rs) => rs.filter((r) => r.id !== id));
  };
  const addRow = () => {
    setRows((rs) => [...rs, { id: shortId("s"), label: "New service", price: 0, duration: 30, aerona_appointment_type_id: "", category_ids: [], sort_order: (rs.length + 1) }]);
  };

  const handleSave = () => {
    // Normalise: ensure category_ids exists (migrate any legacy category_id).
    const ordered = rows.map((r, i) => {
      const cat_ids = r.category_ids && r.category_ids.length
        ? r.category_ids
        : (r.category_id ? [r.category_id] : []);
      const { category_id, ...rest } = r;  
      void category_id;
      return { ...rest, category_ids: cat_ids, sort_order: i + 1 };
    });
    onSave({ services: ordered });
  };

  return (
    <div data-testid="services-pane">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h3 className="text-lg font-bold text-[#0466ff]" style={{ fontFamily: "Manrope" }}>Appointment type IDs</h3>
        <button onClick={addRow} className="ws-btn-outline inline-flex items-center gap-2 text-sm" data-testid="services-add-row">
          <Plus className="w-4 h-4" strokeWidth={2.5} /> ADD SERVICE
        </button>
      </div>
      <p className="text-sm text-[#5C5C5C] mb-4">
        Map each bookable service to its Aerona <code>appointmentTypeId</code>. Tick one or more categories so the chip filter on the customer wizard groups it correctly. Drag to reorder.
      </p>

      <div className="hidden md:grid grid-cols-[28px_1fr_180px_80px_80px_130px_44px] gap-2 items-center px-1 pb-2 mb-1 border-b border-[#E0E0E0]" data-testid="services-headers">
        <div />
        <div className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Service Name</div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Categories</div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Price</div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Time</div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Appointment ID</div>
        <div />
      </div>

      <SortableList
        items={rows}
        onReorder={setRows}
        render={(row, dragHandle) => {
          const currentCatIds = row.category_ids && row.category_ids.length
            ? row.category_ids
            : (row.category_id ? [row.category_id] : []);
          return (
            <div className="grid grid-cols-[28px_1fr_180px_80px_80px_130px_44px] gap-2 items-start" data-testid={`service-row-${row.id}`}>
              {dragHandle}
              <div className="flex flex-col gap-1">
                <input className="ws-input" value={row.label} onChange={(e) => update(row.id, { label: e.target.value })} data-testid={`service-label-${row.id}`} />
                <input
                  className="ws-input text-xs"
                  placeholder="Optional subtext (e.g. 30 mins · Mount Merrion only)"
                  value={row.subtext || ""}
                  onChange={(e) => update(row.id, { subtext: e.target.value })}
                  data-testid={`service-subtext-${row.id}`}
                />
              </div>
              <CategoryMultiSelect
                value={currentCatIds}
                categories={categories}
                onChange={(ids) => update(row.id, { category_ids: ids, category_id: ids[0] || null })}
                testId={`service-category-${row.id}`}
              />
              <input className="ws-input" type="number" placeholder="€ Price" value={row.price ?? ""} onChange={(e) => update(row.id, { price: Number(e.target.value) || 0 })} data-testid={`service-price-${row.id}`} />
              <input className="ws-input" type="number" placeholder="Mins" value={row.duration ?? ""} onChange={(e) => update(row.id, { duration: Number(e.target.value) || 30 })} data-testid={`service-duration-${row.id}`} />
              <input className="ws-input" placeholder="Appointment type ID" value={row.aerona_appointment_type_id || ""} onChange={(e) => update(row.id, { aerona_appointment_type_id: e.target.value.replace(/[^\d]/g, "") })} data-testid={`service-aeronaid-${row.id}`} />
              <button onClick={() => remove(row.id)} className="p-2 rounded-md hover:bg-[#FFF4EE] text-[#C85A41]" title="Delete" data-testid={`service-delete-${row.id}`}>
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          );
        }}
      />

      <SaveBar saving={saving} onClick={handleSave} testId="services-save" />
    </div>
  );
}

// ──────────────────────────── Categories tab ────────────────────────────
function CategoryIconPicker({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const current = value || DEFAULT_CATEGORY_ICON;
  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`ws-input w-full flex items-center justify-center ${open ? "border-[#0466ff]" : ""}`}
        data-testid={testId}
        title="Pick an icon"
      >
        <Icon icon={resolveCategoryIcon(current)} className="w-5 h-5 text-[#0466ff]" />
      </button>
      {open && (
        <div
          className="absolute left-0 mt-1 z-30 bg-white border border-[#E5E5E5] rounded-md shadow-lg p-2 grid grid-cols-6 gap-1 w-72 max-h-72 overflow-y-auto"
          data-testid={`${testId}-popover`}
        >
          {CATEGORY_ICONS.map((opt) => {
            const selected = opt.key === current;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => { onChange(opt.key); setOpen(false); }}
                title={opt.label}
                className={`aspect-square grid place-items-center rounded-md hover:bg-[#F0F6FF] ${selected ? "bg-[#F0F6FF] ring-2 ring-[#0466ff]" : ""}`}
                data-testid={`${testId}-option-${opt.key}`}
              >
                <Icon icon={opt.icon} className={`w-5 h-5 ${selected ? "text-[#0466ff]" : "text-[#1A1A1A]"}`} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CategoriesTab({ config, onSave, saving }) {
  const [rows, setRows] = useState(() => [...(config.categories || [])]);
  const update = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id) => {
    if (!window.confirm("Delete this category? Services using it will become uncategorised.")) return;
    setRows((rs) => rs.filter((r) => r.id !== id));
  };
  const addRow = () => {
    setRows((rs) => [...rs, { id: shortId("c"), label: "New category", icon: DEFAULT_CATEGORY_ICON, sort_order: rs.length + 1 }]);
  };
  const handleSave = () => onSave({ categories: rows.map((r, i) => ({ ...r, sort_order: i + 1 })) });

  return (
    <div data-testid="categories-pane">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h3 className="text-lg font-bold text-[#0466ff]" style={{ fontFamily: "Manrope" }}>Service categories</h3>
        <button onClick={addRow} className="ws-btn-outline inline-flex items-center gap-2 text-sm" data-testid="categories-add-row">
          <Plus className="w-4 h-4" strokeWidth={2.5} /> ADD CATEGORY
        </button>
      </div>
      <p className="text-sm text-[#5C5C5C] mb-4">
        Categories drive the chip filter on the customer wizard. Pick an icon — it shows next to the section title in the patient flow. Drag to reorder.
      </p>
      <SortableList
        items={rows}
        onReorder={setRows}
        render={(row, dragHandle) => (
          <div className="grid grid-cols-[28px_60px_1fr_44px] gap-2 items-center" data-testid={`category-row-${row.id}`}>
            {dragHandle}
            <CategoryIconPicker
              value={row.icon}
              onChange={(icon) => update(row.id, { icon })}
              testId={`category-icon-${row.id}`}
            />
            <input
              className="ws-input"
              placeholder="Category name (e.g. Orthodontics)"
              value={row.label}
              onChange={(e) => update(row.id, { label: e.target.value })}
              data-testid={`category-label-${row.id}`}
            />
            <button onClick={() => remove(row.id)} className="p-2 rounded-md hover:bg-[#FFF4EE] text-[#C85A41]" title="Delete" data-testid={`category-delete-${row.id}`}>
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
      />
      <SaveBar saving={saving} onClick={handleSave} testId="categories-save" />
    </div>
  );
}

// ──────────────────────────── Clinicians (mappings per practice) ────────────────────────────
function CliniciansTab({ config, onSave, saving }) {
  const { practices, services } = config;
  const [mappings, setMappings] = useState(config.mappings || {});
  const [picked, setPicked] = useState(practices[0]?.id);

  const setMapping = (practiceId, serviceId, raw) => {
    // Allow digits and commas only (multi-calendar support)
    const v = raw.replace(/[^\d,]/g, "").replace(/,{2,}/g, ",");
    setMappings((m) => {
      const forPractice = { ...(m[practiceId] || {}) };
      if (v) forPractice[serviceId] = v;
      else delete forPractice[serviceId];
      return { ...m, [practiceId]: forPractice };
    });
  };

  const practice = practices.find((p) => p.id === picked);
  const mapped = mappings[picked] || {};
  const populatedCount = (practiceId) => Object.values(mappings[practiceId] || {}).filter(Boolean).length;

  return (
    <div data-testid="clinicians-pane">
      <h3 className="text-lg font-bold text-[#0466ff] mb-1" style={{ fontFamily: "Manrope" }}>
        Clinician IDs for {practice?.label || "—"}
      </h3>
      <p className="text-sm text-[#5C5C5C] mb-4">
        For each service offered at this practice, enter the Aerona <code>clinicianId</code> (calendar). Leave blank to <em>hide</em> the service from the customer&apos;s location dropdown for that clinic. For services with two chairs (e.g. Teeth Whitening), separate the two clinician IDs with a comma: <code>12418,12419</code>.
      </p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="practice-picker">
        {practices.map((p) => (
          <button
            key={p.id}
            onClick={() => setPicked(p.id)}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${
              picked === p.id ? "border-[#0466ff] bg-[#E8F0FE] text-[#0466ff] font-semibold" : "border-[#E0E0E0] text-[#5C5C5C] hover:border-[#0466ff]/50"
            }`}
            data-testid={`practice-pill-${p.id}`}
          >
            {p.label} <span className="ml-1.5 text-xs opacity-70">({populatedCount(p.id)}/{services.length})</span>
          </button>
        ))}
      </div>

      <div className="space-y-3 max-w-4xl">
        {services.map((svc) => (
          <div key={svc.id} className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-3 items-center" data-testid={`mapping-row-${picked}-${svc.id}`}>
            <div className="text-sm text-[#1A1A1A] font-medium">{svc.label}</div>
            <input
              className="ws-input"
              placeholder="Clinician ID(s)"
              value={mapped[svc.id] || ""}
              onChange={(e) => setMapping(picked, svc.id, e.target.value)}
              data-testid={`mapping-input-${picked}-${svc.id}`}
            />
          </div>
        ))}
      </div>

      <SaveBar saving={saving} onClick={() => onSave({ mappings })} testId="clinicians-save" />
    </div>
  );
}

// ──────────────────────────── Reusable: SortableList ────────────────────────────
function SortableList({ items, onReorder, render }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((item) => (
            <SortableRow key={item.id} id={item.id} render={(handle) => render(item, handle)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ id, render }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    boxShadow: isDragging ? "0 8px 24px rgba(4,102,255,0.15)" : undefined,
    background: isDragging ? "white" : undefined,
    zIndex: isDragging ? 50 : "auto",
  };
  const dragHandle = (
    <button
      ref={setNodeRef === undefined ? undefined : undefined}
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing p-1 text-[#9A9A9A] hover:text-[#0466ff]"
      aria-label="Drag to reorder"
      data-testid={`drag-handle-${id}`}
    >
      <GripVertical className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-md">
      {render(dragHandle)}
    </div>
  );
}

// ──────────────────────────── Reusable: Field + SaveBar ────────────────────────────
function Field({ label, value, onChange, type = "text", dataTestId }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold text-[#0466ff] mb-2" style={{ fontFamily: "Manrope" }}>{label}</label>
      <input className="ws-input" type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={dataTestId} />
    </div>
  );
}

function SaveBar({ saving, onClick, testId }) {
  return (
    <div className="mt-6">
      <button onClick={onClick} disabled={saving} className="ws-btn-primary inline-flex items-center gap-2" data-testid={testId}>
        <Save className="w-4 h-4" strokeWidth={2.5} /> {saving ? "SAVING…" : "SAVE CHANGES"}
      </button>
    </div>
  );
}
